import { useState, useEffect, useCallback } from "react";

// ── Supabase Config ───────────────────────────────────────────────────────────
const SUPABASE_URL = "https://lqnfkimcoqzlbegyuxnj.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxxbmZraW1jb3F6bGJlZ3l1eG5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODUxNDI3MDEsImV4cCI6MjEwMDcxODcwMX0.aPeKRH_D6v8JDpzCaaji8GIeV2s06qxfIwdj-OJDndE";

const sb = async (path, method = "GET", body = null) => {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method,
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      "Content-Type": "application/json",
      Prefer: method === "POST" ? "return=representation" : "return=representation",
    },
    body: body ? JSON.stringify(body) : null,
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : [];
};

// ── Constants ─────────────────────────────────────────────────────────────────
const COLORS = {
  accent: "#6C63FF", success: "#22C55E", warning: "#F59E0B",
  danger: "#EF4444", info: "#38BDF8",
};

const STATUS_META = {
  "Not Started": { color: "#6B7280", next: "Started" },
  "Started":     { color: "#38BDF8", next: "In Progress" },
  "In Progress": { color: "#F59E0B", next: "Sent" },
  "Sent":        { color: "#A78BFA", next: "Completed" },
  "Completed":   { color: "#22C55E", next: null },
};

const CATEGORIES = ["Reel","Logo","Poster","Website","Visiting Card","Video Edit","Photo Edit","Content","Other"];
const TAGS = ["Urgent","VIP","Monthly","One Time","Repeat Client","High Priority"];
const PRIORITIES = ["Low","Medium","High"];

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "";
const today = () => new Date().toDateString();

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [dark, setDark] = useState(true);
  const [clients, setClients] = useState([]);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard");
  const [activeClient, setActiveClient] = useState(null);
  const [activeWork, setActiveWork] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddWork, setShowAddWork] = useState(false);
  const [toast, setToast] = useState(null);

  const T = dark ? DARK : LIGHT;
  const s = styles(T);

  const notify = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // ── Load Data ───────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [cls, wks, revs, progs, tls] = await Promise.all([
        sb("clients?order=created_at.desc"),
        sb("works?order=created_at.desc"),
        sb("revisions?order=created_at.asc"),
        sb("progress?order=created_at.asc"),
        sb("timeline?order=created_at.asc"),
      ]);
      const enriched = wks.map(w => ({
        ...w,
        revisions: revs.filter(r => r.work_id === w.id),
        progress: progs.filter(p => p.work_id === w.id).map(p => p.line),
        timeline: tls.filter(t => t.work_id === w.id).map(t => ({ event: t.event, time: t.created_at })),
        tags: w.tags || [],
      }));
      setClients(cls);
      setWorks(enriched);
    } catch (e) {
      notify("DB Error: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const addClient = async (data) => {
    try {
      const [c] = await sb("clients", "POST", data);
      setClients(p => [c, ...p]);
      notify("Client added!");
    } catch(e) { notify(e.message, "error"); }
  };

  const addWork = async (data) => {
    try {
      const { tags, budget, advance, ...rest } = data;
      const [w] = await sb("works", "POST", { ...rest, tags, budget: Number(budget), advance: Number(advance) });
      await sb("timeline", "POST", { work_id: w.id, event: "Created" });
      const enriched = { ...w, revisions: [], progress: [], timeline: [{ event: "Created", time: new Date().toISOString() }], tags: tags || [] };
      setWorks(p => [enriched, ...p]);
      notify("Work created!");
    } catch(e) { notify(e.message, "error"); }
  };

  const advanceStatus = async (work) => {
    const next = STATUS_META[work.status]?.next;
    if (!next) return;
    try {
      await sb(`works?id=eq.${work.id}`, "PATCH", { status: next });
      await sb("timeline", "POST", { work_id: work.id, event: next });
      const entry = { event: next, time: new Date().toISOString() };
      const updated = { ...work, status: next, timeline: [...work.timeline, entry] };
      setWorks(p => p.map(w => w.id === work.id ? updated : w));
      if (activeWork?.id === work.id) setActiveWork(updated);
      notify(`Status → ${next}`);
    } catch(e) { notify(e.message, "error"); }
  };

  const addRevision = async (work, note) => {
    try {
      const [rev] = await sb("revisions", "POST", { work_id: work.id, note });
      const revNum = work.revisions.length + 1;
      await sb("timeline", "POST", { work_id: work.id, event: `Revision ${revNum}` });
      const entry = { event: `Revision ${revNum}`, time: new Date().toISOString() };
      const updated = { ...work, revisions: [...work.revisions, rev], timeline: [...work.timeline, entry] };
      setWorks(p => p.map(w => w.id === work.id ? updated : w));
      if (activeWork?.id === work.id) setActiveWork(updated);
      notify("Revision added!");
    } catch(e) { notify(e.message, "error"); }
  };

  const addProgress = async (work, line) => {
    try {
      await sb("progress", "POST", { work_id: work.id, line });
      const updated = { ...work, progress: [...work.progress, line] };
      setWorks(p => p.map(w => w.id === work.id ? updated : w));
      if (activeWork?.id === work.id) setActiveWork(updated);
      notify("Progress saved!");
    } catch(e) { notify(e.message, "error"); }
  };

  const toggleDelivered = async (work) => {
    try {
      const val = !work.delivered;
      await sb(`works?id=eq.${work.id}`, "PATCH", { delivered: val });
      if (val) await sb("timeline", "POST", { work_id: work.id, event: "Delivered" });
      const entry = val ? { event: "Delivered", time: new Date().toISOString() } : null;
      const updated = { ...work, delivered: val, timeline: entry ? [...work.timeline, entry] : work.timeline };
      setWorks(p => p.map(w => w.id === work.id ? updated : w));
      if (activeWork?.id === work.id) setActiveWork(updated);
      notify(val ? "Marked Delivered!" : "Delivery undone");
    } catch(e) { notify(e.message, "error"); }
  };

  const togglePaid = async (work) => {
    try {
      const val = !work.paid;
      await sb(`works?id=eq.${work.id}`, "PATCH", { paid: val });
      const updated = { ...work, paid: val };
      setWorks(p => p.map(w => w.id === work.id ? updated : w));
      if (activeWork?.id === work.id) setActiveWork(updated);
      notify(val ? "Payment received!" : "Payment reset");
    } catch(e) { notify(e.message, "error"); }
  };

  const saveNotes = async (work, notes) => {
    try {
      await sb(`works?id=eq.${work.id}`, "PATCH", { notes });
      setWorks(p => p.map(w => w.id === work.id ? { ...w, notes } : w));
    } catch(e) {}
  };

  // ── Computed ────────────────────────────────────────────────────────────────
  const todayWorks = works.filter(w => new Date(w.created_at).toDateString() === today());
  const pending = works.filter(w => w.status !== "Completed");
  const completed = works.filter(w => w.status === "Completed");
  const unpaid = works.filter(w => !w.paid && w.status === "Completed");
  const clientWorks = (cid) => works.filter(w => w.client_id === cid);

  const searchResults = searchQ.length > 1 ? {
    clients: clients.filter(c => `${c.name} ${c.company||""}`.toLowerCase().includes(searchQ.toLowerCase())),
    works: works.filter(w => `${w.title} ${w.category} ${w.status}`.toLowerCase().includes(searchQ.toLowerCase())),
  } : null;

  const openClient = (c) => { setActiveClient(c); setView("client-detail"); };
  const openWork = (w) => { setActiveWork(w); setView("work-detail"); };
  const goBack = () => {
    if (view === "work-detail") { setView("client-detail"); setActiveWork(null); }
    else { setView("clients"); setActiveClient(null); }
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", background: DARK.bg, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <div style={{ width:48, height:48, border:`4px solid ${COLORS.accent}33`, borderTop:`4px solid ${COLORS.accent}`, borderRadius:"50%", animation:"spin 1s linear infinite" }} />
      <div style={{ color: DARK.muted, fontSize:14 }}>Loading FreelanceOS…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={s.shell}>
      {toast && <div style={{ ...s.toast, background: toast.type==="success" ? COLORS.success : COLORS.danger }}>{toast.msg}</div>}

      <header style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.logo}>◈ FreelanceOS</span>
          {(view==="client-detail"||view==="work-detail") && (
            <button style={s.backBtn} onClick={goBack}>← Back</button>
          )}
        </div>
        <div style={s.headerRight}>
          <input style={s.search} placeholder="Search…" value={searchQ}
            onChange={e => { setSearchQ(e.target.value); setView("search"); if(!e.target.value) setView("dashboard"); }} />
          <button style={s.iconBtn} onClick={() => setDark(d=>!d)}>{dark?"☀️":"🌙"}</button>
        </div>
      </header>

      <nav style={s.nav}>
        {[["dashboard","Dashboard"],["clients","Clients"]].map(([k,l]) => (
          <button key={k} style={{...s.navBtn,...(view===k?s.navActive:{})}}
            onClick={() => { setView(k); setSearchQ(""); }}>{l}</button>
        ))}
      </nav>

      <main style={s.main}>

        {/* SEARCH */}
        {view==="search" && searchResults && (
          <div>
            <h2 style={s.sectionTitle}>Results for "{searchQ}"</h2>
            {searchResults.clients.map(c => (
              <div key={c.id} style={s.card} onClick={() => openClient(c)}>
                <strong style={{color:T.text}}>{c.name}</strong>
                <span style={{color:T.muted,marginLeft:8}}>{c.company}</span>
              </div>
            ))}
            {searchResults.works.map(w => (
              <div key={w.id} style={s.card} onClick={() => openWork(w)}>
                <strong style={{color:T.text}}>{w.title}</strong>
                <StatusBadge status={w.status} />
              </div>
            ))}
            {!searchResults.clients.length && !searchResults.works.length && <div style={{color:T.muted}}>No results found.</div>}
          </div>
        )}

        {/* DASHBOARD */}
        {view==="dashboard" && (
          <div>
            <h2 style={s.sectionTitle}>Dashboard</h2>
            <div style={s.statsGrid}>
              <StatCard label="Today's Work" value={todayWorks.length} color={COLORS.accent} T={T} />
              <StatCard label="Pending" value={pending.length} color={COLORS.warning} T={T} />
              <StatCard label="Completed" value={completed.length} color={COLORS.success} T={T} />
              <StatCard label="Pending Payments" value={unpaid.length} color={COLORS.danger} T={T} />
              <StatCard label="Total Clients" value={clients.length} color={COLORS.info} T={T} />
              <StatCard label="Total Works" value={works.length} color="#A78BFA" T={T} />
            </div>
            <div style={s.grid2}>
              <div style={s.panel}>
                <div style={s.panelTitle}>Recent Activity</div>
                {works.slice(0,6).flatMap(w =>
                  (w.timeline||[]).slice(-2).map((t,i) => (
                    <div key={w.id+i} style={s.activityRow}>
                      <span style={{color:COLORS.accent,fontSize:11}}>{fmt(t.time)}</span>
                      <span style={{color:T.text,marginLeft:8}}>{w.title} → <b>{t.event}</b></span>
                    </div>
                  ))
                ).slice(0,8)}
              </div>
              <div style={s.panel}>
                <div style={s.panelTitle}>Pending Payments</div>
                {unpaid.length===0 && <div style={{color:T.muted,fontSize:13}}>All payments cleared ✓</div>}
                {unpaid.map(w => {
                  const cl = clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id} style={s.payRow} onClick={()=>openWork(w)}>
                      <div>
                        <div style={{color:T.text,fontWeight:600}}>{w.title}</div>
                        <div style={{color:T.muted,fontSize:12}}>{cl?.name}</div>
                      </div>
                      <div style={{color:COLORS.danger,fontWeight:700}}>₹{(w.budget-w.advance).toLocaleString()}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CLIENTS */}
        {view==="clients" && (
          <div>
            <div style={s.rowBetween}>
              <h2 style={s.sectionTitle}>Clients</h2>
              <button style={s.primaryBtn} onClick={()=>setShowAddClient(true)}>+ Add Client</button>
            </div>
            {clients.map(c => {
              const cw = clientWorks(c.id);
              return (
                <div key={c.id} style={s.clientCard} onClick={()=>openClient(c)}>
                  <div style={s.avatar}>{c.name[0]}</div>
                  <div style={{flex:1}}>
                    <div style={{color:T.text,fontWeight:700,fontSize:15}}>{c.name}</div>
                    {c.company && <div style={{color:T.muted,fontSize:12}}>{c.company}</div>}
                    <div style={{color:T.muted,fontSize:12}}>{c.phone}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{color:COLORS.accent,fontWeight:700}}>{cw.length} works</div>
                    <div style={{color:COLORS.success,fontSize:12}}>{cw.filter(w=>w.status==="Completed").length} done</div>
                  </div>
                </div>
              );
            })}
            {showAddClient && <AddClientModal T={T} onSave={async d=>{await addClient(d);setShowAddClient(false);}} onClose={()=>setShowAddClient(false)} />}
          </div>
        )}

        {/* CLIENT DETAIL */}
        {view==="client-detail" && activeClient && (
          <div>
            <div style={s.clientHeader}>
              <div style={{...s.avatar,width:56,height:56,fontSize:24}}>{activeClient.name[0]}</div>
              <div>
                <div style={{color:T.text,fontWeight:800,fontSize:20}}>{activeClient.name}</div>
                {activeClient.company && <div style={{color:T.muted}}>{activeClient.company}</div>}
                <div style={{color:T.muted,fontSize:13}}>{activeClient.phone} · {activeClient.email}</div>
                {activeClient.notes && <div style={{color:COLORS.warning,fontSize:13,marginTop:4}}>📝 {activeClient.notes}</div>}
              </div>
            </div>
            <div style={s.rowBetween}>
              <div style={s.panelTitle}>Works</div>
              <button style={s.primaryBtn} onClick={()=>setShowAddWork(true)}>+ Add Work</button>
            </div>
            {clientWorks(activeClient.id).map(w => (
              <div key={w.id} style={s.workCard} onClick={()=>openWork(w)}>
                <div style={s.rowBetween}>
                  <div style={{color:T.text,fontWeight:700}}>{w.title}</div>
                  <StatusBadge status={w.status} />
                </div>
                <div style={{color:T.muted,fontSize:12,marginTop:4}}>{w.category} · Due {w.deadline}</div>
                <div style={s.rowBetween}>
                  <PriorityDots priority={w.priority} />
                  <span style={{color:w.paid?COLORS.success:COLORS.danger,fontSize:12,fontWeight:600}}>
                    ₹{(w.budget||0).toLocaleString()} {w.paid?"✓ Paid":"· Pending"}
                  </span>
                </div>
              </div>
            ))}
            {showAddWork && (
              <AddWorkModal T={T} clientId={activeClient.id}
                onSave={async d=>{await addWork(d);setShowAddWork(false);}}
                onClose={()=>setShowAddWork(false)} />
            )}
          </div>
        )}

        {/* WORK DETAIL */}
        {view==="work-detail" && activeWork && (
          <WorkDetail work={activeWork} T={T} s={s}
            onAdvance={()=>advanceStatus(activeWork)}
            onRevision={note=>addRevision(activeWork,note)}
            onProgress={line=>addProgress(activeWork,line)}
            onDelivery={()=>toggleDelivered(activeWork)}
            onPayment={()=>togglePaid(activeWork)}
            onNoteChange={notes=>saveNotes(activeWork,notes)}
          />
        )}

      </main>
    </div>
  );
}

// ── Work Detail ───────────────────────────────────────────────────────────────
function WorkDetail({ work, T, s, onAdvance, onRevision, onProgress, onDelivery, onPayment, onNoteChange }) {
  const [revNote, setRevNote] = useState("");
  const [progLine, setProgLine] = useState("");
  const [noteText, setNoteText] = useState(work.notes||"");
  const meta = STATUS_META[work.status];

  return (
    <div>
      <div style={s.rowBetween}>
        <div>
          <h2 style={{...s.sectionTitle,marginBottom:4}}>{work.title}</h2>
          <div style={{color:T.muted,fontSize:13}}>{work.category} · Due {work.deadline}</div>
        </div>
        <PriorityDots priority={work.priority} />
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",margin:"10px 0"}}>
        {(work.tags||[]).map(t=><span key={t} style={s.tag}>{t}</span>)}
      </div>
      <div style={s.section}>
        <div style={s.panelTitle}>Status</div>
        <div style={s.rowBetween}>
          <StatusBadge status={work.status} large />
          {meta?.next && (
            <button style={{...s.primaryBtn,background:STATUS_META[meta.next]?.color||COLORS.accent}} onClick={onAdvance}>
              → Move to {meta.next}
            </button>
          )}
        </div>
      </div>
      <div style={{...s.grid2,gap:10}}>
        <button style={{...s.toggleBtn,background:work.delivered?COLORS.success:T.card,color:work.delivered?"#fff":T.muted,border:`1.5px solid ${work.delivered?COLORS.success:T.border}`}} onClick={onDelivery}>
          {work.delivered?"✓ Delivered":"Mark Delivered"}
        </button>
        <button style={{...s.toggleBtn,background:work.paid?COLORS.success:T.card,color:work.paid?"#fff":T.muted,border:`1.5px solid ${work.paid?COLORS.success:T.border}`}} onClick={onPayment}>
          {work.paid?"✓ Payment Received":"Mark Payment Received"}
        </button>
      </div>
      <div style={s.payCard}>
        <PayRow label="Budget" value={`₹${(work.budget||0).toLocaleString()}`} T={T} />
        <PayRow label="Advance" value={`₹${(work.advance||0).toLocaleString()}`} T={T} color={COLORS.success} />
        <PayRow label="Remaining" value={`₹${((work.budget||0)-(work.advance||0)).toLocaleString()}`} T={T} color={work.paid?COLORS.success:COLORS.danger} />
      </div>
      <div style={s.section}>
        <div style={s.panelTitle}>Revisions ({(work.revisions||[]).length})</div>
        {(work.revisions||[]).map((r,i)=>(
          <div key={r.id} style={s.revRow}>
            <span style={{color:COLORS.accent,fontWeight:700,fontSize:13}}>Rev {i+1}</span>
            <span style={{color:T.text,marginLeft:10}}>{r.note}</span>
            <span style={{color:T.muted,fontSize:11,marginLeft:"auto"}}>{fmt(r.created_at)}</span>
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:10}}>
          <input style={s.input} placeholder="Short revision note…" value={revNote} onChange={e=>setRevNote(e.target.value)} />
          <button style={s.primaryBtn} onClick={()=>{if(revNote.trim()){onRevision(revNote.trim());setRevNote("");}}}>+ Revision</button>
        </div>
      </div>
      <div style={s.section}>
        <div style={s.panelTitle}>Daily Progress</div>
        {(work.progress||[]).map((p,i)=>(
          <div key={i} style={{color:T.muted,fontSize:13,padding:"3px 0",borderBottom:`1px solid ${T.border}`}}>· {p}</div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:10}}>
          <input style={s.input} placeholder="One line update…" value={progLine} onChange={e=>setProgLine(e.target.value)} />
          <button style={s.primaryBtn} onClick={()=>{if(progLine.trim()){onProgress(progLine.trim());setProgLine("");}}}>Save</button>
        </div>
      </div>
      <div style={s.section}>
        <div style={s.panelTitle}>Notes</div>
        <textarea style={{...s.input,height:80,resize:"vertical"}} value={noteText}
          onChange={e=>{setNoteText(e.target.value);onNoteChange(e.target.value);}} placeholder="Client notes, reminders…" />
      </div>
      <div style={s.section}>
        <div style={s.panelTitle}>Timeline</div>
        {(work.timeline||[]).map((t,i)=>(
          <div key={i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:`1px solid ${T.border}`}}>
            <span style={{color:COLORS.accent,fontSize:12,minWidth:130}}>{fmt(t.time)}</span>
            <span style={{color:T.text,fontSize:13}}>{t.event}</span>
          </div>
        ))}
      </div>
      {work.description && (
        <div style={s.section}>
          <div style={s.panelTitle}>Description</div>
          <div style={{color:T.muted,fontSize:14}}>{work.description}</div>
        </div>
      )}
    </div>
  );
}

// ── Modals ────────────────────────────────────────────────────────────────────
function AddClientModal({ T, onSave, onClose }) {
  const [form, setForm] = useState({ name:"", phone:"", email:"", company:"", notes:"" });
  const s = styles(T);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  return (
    <Modal title="Add Client" T={T} onClose={onClose} onSave={()=>{if(form.name)onSave(form);}}>
      <input style={s.input} placeholder="Client Name *" value={form.name} onChange={set("name")} />
      <input style={s.input} placeholder="Phone" value={form.phone} onChange={set("phone")} />
      <input style={s.input} placeholder="Email" value={form.email} onChange={set("email")} />
      <input style={s.input} placeholder="Company (optional)" value={form.company} onChange={set("company")} />
      <textarea style={{...s.input,height:60}} placeholder="Notes" value={form.notes} onChange={set("notes")} />
    </Modal>
  );
}

function AddWorkModal({ T, clientId, onSave, onClose }) {
  const [form, setForm] = useState({ client_id:clientId, title:"", category:"Reel", description:"", deadline:"", priority:"Medium", tags:[], budget:0, advance:0 });
  const s = styles(T);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const toggleTag = t => setForm(f=>({...f,tags:f.tags.includes(t)?f.tags.filter(x=>x!==t):[...f.tags,t]}));
  return (
    <Modal title="Add Work" T={T} onClose={onClose} onSave={()=>{if(form.title)onSave(form);}}>
      <input style={s.input} placeholder="Work Title *" value={form.title} onChange={set("title")} />
      <select style={s.input} value={form.category} onChange={set("category")}>
        {CATEGORIES.map(c=><option key={c}>{c}</option>)}
      </select>
      <textarea style={{...s.input,height:60}} placeholder="Description" value={form.description} onChange={set("description")} />
      <input style={s.input} type="date" value={form.deadline} onChange={set("deadline")} />
      <div style={{color:T.muted,fontSize:12,marginBottom:4}}>Priority</div>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        {PRIORITIES.map(p=>(
          <button key={p} style={{...s.chipBtn,...(form.priority===p?{background:COLORS.accent,color:"#fff",border:"none"}:{})}}
            onClick={()=>setForm(f=>({...f,priority:p}))}>
            {"⭐".repeat(PRIORITIES.indexOf(p)+1)} {p}
          </button>
        ))}
      </div>
      <div style={{color:T.muted,fontSize:12,marginBottom:4}}>Tags</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
        {TAGS.map(t=>(
          <button key={t} style={{...s.chipBtn,...(form.tags.includes(t)?{background:COLORS.accent,color:"#fff",border:"none"}:{})}}
            onClick={()=>toggleTag(t)}>{t}</button>
        ))}
      </div>
      <div style={{display:"flex",gap:8}}>
        <input style={s.input} type="number" placeholder="Budget ₹" value={form.budget||""} onChange={e=>setForm(f=>({...f,budget:e.target.value}))} />
        <input style={s.input} type="number" placeholder="Advance ₹" value={form.advance||""} onChange={e=>setForm(f=>({...f,advance:e.target.value}))} />
      </div>
    </Modal>
  );
}

function Modal({ title, T, onClose, onSave, children }) {
  const s = styles(T);
  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.rowBetween}>
          <div style={{color:T.text,fontWeight:700,fontSize:16}}>{title}</div>
          <button style={s.iconBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:10}}>{children}</div>
        <div style={{display:"flex",gap:10,marginTop:16}}>
          <button style={{...s.primaryBtn,flex:1}} onClick={onSave}>Save</button>
          <button style={{...s.ghostBtn,flex:1}} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Small Components ──────────────────────────────────────────────────────────
function StatCard({ label, value, color, T }) {
  return (
    <div style={{background:T.card,borderRadius:12,padding:"18px 20px",borderLeft:`4px solid ${color}`}}>
      <div style={{color,fontSize:28,fontWeight:800}}>{value}</div>
      <div style={{color:T.muted,fontSize:12,marginTop:2}}>{label}</div>
    </div>
  );
}

function StatusBadge({ status, large }) {
  const color = STATUS_META[status]?.color||"#6B7280";
  return (
    <span style={{background:color+"22",color,border:`1px solid ${color}55`,borderRadius:20,padding:large?"5px 14px":"3px 10px",fontSize:large?13:11,fontWeight:700}}>
      {status}
    </span>
  );
}

function PriorityDots({ priority }) {
  const n = PRIORITIES.indexOf(priority)+1;
  const colors = ["#22C55E","#F59E0B","#EF4444"];
  return <span>{Array.from({length:n}).map((_,i)=><span key={i} style={{color:colors[PRIORITIES.indexOf(priority)],fontSize:13}}>●</span>)}</span>;
}

function PayRow({ label, value, T, color }) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0"}}>
      <span style={{color:T.muted,fontSize:13}}>{label}</span>
      <span style={{color:color||T.text,fontWeight:700,fontSize:14}}>{value}</span>
    </div>
  );
}

// ── Themes ────────────────────────────────────────────────────────────────────
const DARK = { bg:"#0F1117", surface:"#1A1D27", card:"#222636", text:"#F0F0F5", muted:"#6B7280", border:"#2D3148", input:"#181B28" };
const LIGHT = { bg:"#F5F6FA", surface:"#FFFFFF", card:"#EEEEF5", text:"#111827", muted:"#6B7280", border:"#D1D5DB", input:"#F9FAFB" };

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = T => ({
  shell: { minHeight:"100vh", background:T.bg, fontFamily:"'Inter','Segoe UI',sans-serif", color:T.text },
  header: { background:T.surface, borderBottom:`1px solid ${T.border}`, padding:"12px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:100 },
  headerLeft: { display:"flex", alignItems:"center", gap:16 },
  headerRight: { display:"flex", alignItems:"center", gap:10 },
  logo: { fontWeight:900, fontSize:18, color:COLORS.accent, letterSpacing:"-0.5px" },
  backBtn: { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:8, padding:"5px 12px", cursor:"pointer", fontSize:13 },
  search: { background:T.input, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, padding:"7px 12px", fontSize:13, outline:"none", width:200 },
  iconBtn: { background:"none", border:"none", cursor:"pointer", fontSize:18, padding:4 },
  nav: { background:T.surface, borderBottom:`1px solid ${T.border}`, padding:"0 20px", display:"flex" },
  navBtn: { background:"none", border:"none", color:T.muted, padding:"12px 16px", cursor:"pointer", fontSize:14, fontWeight:600, borderBottom:"3px solid transparent" },
  navActive: { color:COLORS.accent, borderBottom:`3px solid ${COLORS.accent}` },
  main: { padding:"24px 20px", maxWidth:820, margin:"0 auto" },
  sectionTitle: { color:T.text, fontWeight:800, fontSize:22, margin:"0 0 20px" },
  statsGrid: { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24 },
  grid2: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginTop:16 },
  panel: { background:T.surface, borderRadius:12, padding:16, border:`1px solid ${T.border}` },
  panelTitle: { color:T.text, fontWeight:700, fontSize:14, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.05em" },
  card: { background:T.card, borderRadius:10, padding:"12px 16px", marginBottom:8, cursor:"pointer", display:"flex", alignItems:"center", gap:10, border:`1px solid ${T.border}` },
  clientCard: { background:T.surface, borderRadius:12, padding:"14px 16px", marginBottom:10, cursor:"pointer", display:"flex", alignItems:"center", gap:14, border:`1px solid ${T.border}` },
  workCard: { background:T.surface, borderRadius:12, padding:14, marginBottom:10, cursor:"pointer", border:`1px solid ${T.border}`, display:"flex", flexDirection:"column", gap:8 },
  avatar: { width:44, height:44, borderRadius:"50%", background:COLORS.accent+"33", color:COLORS.accent, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:18, flexShrink:0 },
  clientHeader: { display:"flex", gap:16, alignItems:"flex-start", marginBottom:24, background:T.surface, borderRadius:14, padding:18, border:`1px solid ${T.border}` },
  section: { background:T.surface, borderRadius:12, padding:16, marginTop:14, border:`1px solid ${T.border}` },
  payCard: { background:T.card, borderRadius:12, padding:"12px 16px", marginTop:14, border:`1px solid ${T.border}` },
  revRow: { display:"flex", alignItems:"center", padding:"7px 0", borderBottom:`1px solid ${T.border}`, fontSize:13 },
  activityRow: { display:"flex", alignItems:"center", padding:"5px 0", borderBottom:`1px solid ${T.border}`, fontSize:12 },
  payRow: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${T.border}`, cursor:"pointer" },
  tag: { background:COLORS.accent+"22", color:COLORS.accent, border:`1px solid ${COLORS.accent}44`, borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:600 },
  input: { background:T.input, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, padding:"9px 12px", fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", fontFamily:"inherit" },
  primaryBtn: { background:COLORS.accent, color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontWeight:700, cursor:"pointer", fontSize:13, whiteSpace:"nowrap" },
  ghostBtn: { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:8, padding:"9px 18px", fontWeight:600, cursor:"pointer", fontSize:13 },
  toggleBtn: { borderRadius:10, padding:"12px 0", fontWeight:700, cursor:"pointer", fontSize:13, textAlign:"center" },
  chipBtn: { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:20, padding:"5px 12px", fontSize:12, cursor:"pointer" },
  rowBetween: { display:"flex", justifyContent:"space-between", alignItems:"center" },
  overlay: { position:"fixed", inset:0, background:"#00000088", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:20 },
  modal: { background:T.surface, borderRadius:16, padding:24, width:"100%", maxWidth:440, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${T.border}` },
  toast: { position:"fixed", bottom:24, right:24, padding:"12px 20px", borderRadius:10, color:"#fff", fontWeight:700, fontSize:13, zIndex:300, boxShadow:"0 4px 20px #0006" },
});
